import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Col, Row, Button, FormGroup, Label } from "reactstrap";
import LineEditorTables from "./lineEditorTables";
import QuoteExportPreview from "./quoteExportPreview";
import QuoteContent from "./quoteContent";
import { useExportRequestData } from "../../../hooks/useExportRequestData";
import CustomSelect from "../../../components/CustomSelect";
import { flattenTemplateFields, resolveEffectiveTemplateState } from "../../adminExports/data/customFieldsTree";

const PINNED_SECTION = "Section 1";

const normalizeSectionName = (sectionName) => (sectionName || "").trim() || PINNED_SECTION;

const QuoteEditor = ({
    quoteRevision,
    quoteItems, quoteCustomisation,
    onUpfrontGroupMoved, onUpfrontItemMoved, onAddUpfrontGroup, onDeleteUpfrontGroup, onUpfrontGroupExpandedChanged,
    onRecurringGroupMoved, onRecurringItemMoved, onAddRecurringGroup, onDeleteRecurringGroup, onRecurringGroupExpandedChanged,
    onEditRecurringGroup, onEditUpfrontGroup, onUpdateRecurringVisibleGroupOrItem, onUpdateUpfrontVisibleGroupOrItem,
    onUpdateQuoteTemplateId, onUpdateQuoteAdditionalFields, onUpdateUpfrontShowChildren, onUpdateRecurringShowChildren
}) => {
    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("Line Editor");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const { salesForceData, customContent, exportData, upfrontItems, recurringItems, isLoadingOpportunity, isLoadingTemplates, templatesData } =
        useExportRequestData(quoteRevision, quoteItems, quoteCustomisation, selectedTemplate);

    useEffect(() => {
        if (templatesData?.length && quoteRevision?.quoteTemplateId) {
            const templateId = quoteRevision?.quoteTemplateId;
            if (!templateId || !templatesData?.length) return;

            const foundTemplate = templatesData.find(t => t.id === templateId) ?? null;
            if (!foundTemplate) return;
            foundTemplate.QuoteTemplateTags = [];
            setSelectedTemplate(foundTemplate);
        }

    }, [quoteRevision?.quoteTemplateId, templatesData]);

    // --- derived loading & readiness flags ---
    const isAnyLoading =
        isLoadingTemplates ||
        isLoadingOpportunity; // (will be false when not enabled)

    const templateSectionTabs = useMemo(() => {
        if (!selectedTemplate) return [];

        const sectionLinks = flattenTemplateFields(selectedTemplate.customFields, selectedTemplate.customSections, selectedTemplate.customFieldsLayout);
        const sectionsConfig = resolveEffectiveTemplateState(selectedTemplate.customFields, selectedTemplate.customSections, selectedTemplate.customFieldsLayout).sections;

        const sectionNameByGuid = new Map();
        sectionsConfig.forEach((section) => {
            if (!section?.guid) return;
            sectionNameByGuid.set(section.guid, normalizeSectionName(section?.name));
        });

        const tabNames = [];
        sectionLinks.forEach((link) => {
            const sectionGuid = String(link?.sectionGuid ?? "").trim();
            const sectionName = sectionNameByGuid.get(sectionGuid) || PINNED_SECTION;
            if (!tabNames.includes(sectionName)) {
                tabNames.push(sectionName);
            }
        });

        return tabNames.length ? tabNames : ["Template Inputs"];
    }, [selectedTemplate]);

    const editorTabs = useMemo(() => ["Line Editor", ...templateSectionTabs], [templateSectionTabs]);
    const quoteTemplateOptions = useMemo(
        () => (Array.isArray(templatesData) ? templatesData : [])
            .filter((template) => template?.type === "quote" && !template?.disabled)
            .map((template) => ({
                value: `quote-${template.id}`,
                label: template.name,
                template,
            })),
        [templatesData]
    );

    const selectedTemplateValue = selectedTemplate ? `quote-${selectedTemplate.id}` : null;

    useEffect(() => {
        if (!editorTabs.includes(currentActiveMainTab)) {
            setCurrentActiveMainTab("Line Editor");
        }
    }, [currentActiveMainTab, editorTabs]);

    // const hasQuoteItems = Array.isArray(quoteItems) && quoteItems.length > 0;
    // const isTemplateReady = !!selectedTemplate;
    // const exportHasData =
    //     !!exportData &&
    //     Array.isArray(exportData.upfront) &&
    //     Array.isArray(exportData.recurring) &&
    //     (exportData.upfront.length > 0 || exportData.recurring.length > 0);

    // // Only show the preview when everything we need is present
    // const previewReady =
    //     !isAnyLoading &&
    //     !quoteRevisionError &&
    //     !templatesError &&
    //     !opportunityError &&
    //     isTemplateReady &&
    //     hasQuoteItems &&
    //     exportHasData;

    return (
        <div data-testid="quote-editor-scroll-area">
            <Row className="mx-0 align-items-start">
                <Col className="d-flex flex-column" style={{ minHeight: 0 }}>
                    <Card className="flex-grow-1 mb-0" style={{ minHeight: 0 }}>
                        <CardHeader>
                            <FormGroup className="mb-3 mt-2">
                                <Label className="fw-semibold">Quote Template</Label>
                                {quoteTemplateOptions?.length === 0 && (
                                    <div className="text-muted small">
                                        {isLoadingTemplates ? "Loading Quote Templates..." : "No enabled quote templates"}
                                    </div>
                                )}
                                {quoteTemplateOptions?.length > 0 && (
                                <CustomSelect
                                    value={selectedTemplateValue}
                                    options={quoteTemplateOptions}
                                    onChange={(opt) => {
                                        const nextTemplate = opt?.template ?? null;
                                        setSelectedTemplate(nextTemplate);
                                        onUpdateQuoteTemplateId(nextTemplate?.id ?? null);
                                    }}
                                    placeholder="< Please select >"
                                    data-testid="quote-editor-template-select"
                                />
                                )}
                            </FormGroup>
                            <Row>
                                <Col style={{ display: "inline-flex" }}>
                                    {editorTabs.map((tab) => (
                                        <h6
                                            key={tab}
                                            className={
                                                currentActiveMainTab === tab
                                                    ? "custom-nav-selected custom-nav m-3"
                                                    : "custom-nav m-3"
                                            }
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setCurrentActiveMainTab(tab)}
                                        >
                                            {tab}
                                        </h6>
                                    ))}
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody
                            className="d-flex flex-column flex-grow-1"
                            style={{ minHeight: 0 }}
                        >
                            {currentActiveMainTab === "Line Editor" ? (
                                <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                    <LineEditorTables
                                        upfrontGroups={quoteCustomisation.groups.upfront}
                                        recurringGroups={quoteCustomisation.groups.recurring}
                                        upfrontItems={upfrontItems}
                                        recurringItems={recurringItems}
                                        groups={quoteCustomisation.groups ?? { upfront: [], recurring: [] }}
                                        onUpfrontGroupMoved={onUpfrontGroupMoved}
                                        onUpfrontItemMoved={onUpfrontItemMoved}
                                        onAddUpfrontGroup={onAddUpfrontGroup}
                                        onDeleteUpfrontGroup={onDeleteUpfrontGroup}
                                        onRecurringGroupMoved={onRecurringGroupMoved}
                                        onRecurringItemMoved={onRecurringItemMoved}
                                        onAddRecurringGroup={onAddRecurringGroup}
                                        onDeleteRecurringGroup={onDeleteRecurringGroup}
                                        onUpfrontGroupExpandedChanged={onUpfrontGroupExpandedChanged}
                                        onRecurringGroupExpandedChanged={onRecurringGroupExpandedChanged}
                                        onEditRecurringGroup={onEditRecurringGroup}
                                        onEditUpfrontGroup={onEditUpfrontGroup}
                                        onUpdateRecurringVisibleGroupOrItem={onUpdateRecurringVisibleGroupOrItem}
                                        onUpdateUpfrontVisibleGroupOrItem={onUpdateUpfrontVisibleGroupOrItem}
                                        onUpdateUpfrontShowChildren={onUpdateUpfrontShowChildren}
                                        onUpdateRecurringShowChildren={onUpdateRecurringShowChildren}
                                        mode="flat"
                                    />
                                </div>
                            ) : currentActiveMainTab === "Line Editor (NestedDataTable)" ? (
                                <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                    <LineEditorTables
                                        quoteItems={quoteItems}
                                        groups={quoteCustomisation.groups ?? { upfront: [], recurring: [] }}
                                        onUpfrontGroupMoved={onUpfrontGroupMoved}
                                        onUpfrontItemMoved={onUpfrontItemMoved}
                                        onAddUpfrontGroup={onAddUpfrontGroup}
                                        onDeleteUpfrontGroup={onDeleteUpfrontGroup}
                                        onRecurringGroupMoved={onRecurringGroupMoved}
                                        onRecurringItemMoved={onRecurringItemMoved}
                                        onAddRecurringGroup={onAddRecurringGroup}
                                        onDeleteRecurringGroup={onDeleteRecurringGroup}
                                        onUpfrontGroupExpandedChanged={onUpfrontGroupExpandedChanged}
                                        onRecurringGroupExpandedChanged={onRecurringGroupExpandedChanged}
                                        mode="nested"
                                    />
                                </div>
                            ) : (
                                <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                    <QuoteContent
                                        key={selectedTemplate?.id ?? "no-template"}
                                        quoteRevision={quoteRevision}
                                        templatesData={templatesData}
                                        selectedTemplate={selectedTemplate}
                                        setSelectedTemplate={setSelectedTemplate}
                                        onUpdateQuoteTemplateId={onUpdateQuoteTemplateId}
                                        onUpdateQuoteAdditionalFields={onUpdateQuoteAdditionalFields}
                                        customContent={customContent}
                                        activeSectionName={currentActiveMainTab === "Template Inputs" ? undefined : currentActiveMainTab}
                                        hideSectionTabs={currentActiveMainTab !== "Template Inputs"}
                                    />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                <Col className="d-flex flex-column" style={{ height: "max(400px, calc(100dvh - 260px))", minHeight: 0 }}>
                    {isAnyLoading ? (
                        <div style={{ width: "100%", height: "100%", minHeight: 0 }}>
                            <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-3 mb-0">Loading…</p>
                            </div>
                        </div>
                    ) : (
                        <QuoteExportPreview
                            quoteItems={quoteItems}
                            groupedQuoteItems={exportData}
                            salesForceData={salesForceData}
                            customContent={customContent}
                            template={selectedTemplate}
                            allowDownload={false}
                        />
                    )}

                </Col>
            </Row>
        </div>

    );
}
export default QuoteEditor;